/**
 * Utility for building filter info text for chart headers
 */

import type { FilterOption } from '../types';
import { einzelplanName, lookupLabel, t } from '../i18n';

export interface ActiveFilterValues {
  einzelplan: string | null;
  kapitel: string | null;
  funktion: string | null;
  oberfunktion: string | null;
  hauptfunktion: string | null;
  gruppierung: string | null;
  obergruppe: string | null;
  hauptgruppe: string | null;
  // Titel prefix letter (e.g. "B" = Bereichsausnahme), set by spotlights.
  prefix: string | null;
  // Chapter-set scope ('kernhaushalt'), set by the Kernhaushalt spotlight.
  kapitelScope: string | null;
  query: string | null;
}

export interface FilterOptionsLookup {
  einzelplan: FilterOption[];
  kapitel: FilterOption[];
  funktion: FilterOption[];
  oberfunktion: FilterOption[];
  hauptfunktion: FilterOption[];
  gruppierung: FilterOption[];
  obergruppe: FilterOption[];
  hauptgruppe: FilterOption[];
}

/**
 * Builds a human-readable filter info string from active filters.
 * Returns null if no filters are active.
 *
 * Format: "Gefiltert nach: Einzelplan 60 - Allgemeine Finanzverwaltung, Kapitel 92 - Name, ..."
 * (in the widget language; Kapitel/Funktion/Gruppierung texts stay German).
 */
export function buildFilterInfoText(
  filters: ActiveFilterValues | undefined,
  filterOptions: FilterOptionsLookup | null | undefined
): string | null {
  if (!filters) return null;

  const m = t().filterInfo;
  const parts: string[] = [];

  // Einzelplan filter
  if (filters.einzelplan) {
    const option = filterOptions?.einzelplan.find(o => o.value === filters.einzelplan);
    if (option) {
      parts.push(`${m.einzelplan} ${einzelplanName(option.label)}`);
    } else {
      parts.push(`${m.einzelplan} ${filters.einzelplan}`);
    }
  }

  // Kapitel filter (format: "einzelplan|kapitel")
  if (filters.kapitel) {
    const option = filterOptions?.kapitel.find(o => o.value === filters.kapitel);
    if (option) {
      parts.push(`${m.kapitel} ${lookupLabel('kapitel', option.label)}`);
    } else {
      // Extract kapitel code from compound value
      const kapitelCode = filters.kapitel.includes('|')
        ? filters.kapitel.split('|')[1]
        : filters.kapitel;
      parts.push(`${m.kapitel} ${kapitelCode}`);
    }
  }

  // Funktion hierarchy filters (show most specific)
  if (filters.funktion) {
    const option = filterOptions?.funktion?.find(o => o.value === filters.funktion);
    if (option) {
      parts.push(`${m.funktion} ${lookupLabel('funktion', option.label)}`);
    } else {
      parts.push(`${m.funktion} ${filters.funktion}`);
    }
  } else if (filters.oberfunktion) {
    const option = filterOptions?.oberfunktion?.find(o => o.value === filters.oberfunktion);
    if (option) {
      parts.push(`${m.oberfunktion} ${lookupLabel('funktion', option.label)}`);
    } else {
      parts.push(`${m.oberfunktion} ${filters.oberfunktion}`);
    }
  } else if (filters.hauptfunktion) {
    const option = filterOptions?.hauptfunktion?.find(o => o.value === filters.hauptfunktion);
    if (option) {
      parts.push(`${m.hauptfunktion} ${lookupLabel('funktion', option.label)}`);
    } else {
      parts.push(`${m.hauptfunktion} ${filters.hauptfunktion}`);
    }
  }

  // Gruppierung hierarchy filters (show most specific)
  if (filters.gruppierung) {
    const option = filterOptions?.gruppierung?.find(o => o.value === filters.gruppierung);
    if (option) {
      parts.push(`${m.gruppierung} ${lookupLabel('gruppierung', option.label)}`);
    } else {
      parts.push(`${m.gruppierung} ${filters.gruppierung}`);
    }
  } else if (filters.obergruppe) {
    const option = filterOptions?.obergruppe?.find(o => o.value === filters.obergruppe);
    if (option) {
      parts.push(`${m.obergruppe} ${lookupLabel('gruppierung', option.label)}`);
    } else {
      parts.push(`${m.obergruppe} ${filters.obergruppe}`);
    }
  } else if (filters.hauptgruppe) {
    const option = filterOptions?.hauptgruppe?.find(o => o.value === filters.hauptgruppe);
    if (option) {
      parts.push(`${m.hauptgruppe} ${lookupLabel('gruppierung', option.label)}`);
    } else {
      parts.push(`${m.hauptgruppe} ${filters.hauptgruppe}`);
    }
  }

  // Titel prefix filter (spotlights: "B" = Bereichsausnahme)
  if (filters.prefix) {
    parts.push(m.prefixLabels[filters.prefix] ?? m.titlePrefix(filters.prefix));
  }

  // Chapter-set scope (spotlight: the budget without the annex chapters)
  if (filters.kapitelScope === 'kernhaushalt') {
    parts.push(m.kernhaushalt);
  }

  // Search query
  if (filters.query) {
    parts.push(m.query(filters.query));
  }

  if (parts.length === 0) return null;

  return m.prefix(parts.join(', '));
}
